import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { cn } from '@/utils/cn';
import { formatCompactFollowers, formatCurrency } from '@/utils/formatters';
import { categoryLabel } from '@/utils/creator-helpers';
import { pbFileUrl } from '@/utils/pb-file-url';
import { useMemberDisplay } from '@/hooks/use-member-display';
import type { MemberDisplay } from '@/utils/member-display';
import type { LeaderboardRow } from '../types';

const TD = 'px-md py-sm';
const NUM = `${TD} whitespace-nowrap font-mono text-xs tabular-nums`;
const TR = 'border-b border-white/[0.03] transition-colors duration-150';
const TR_CLICK = 'cursor-pointer hover:bg-white/[0.05]';

type Props = {
  row: LeaderboardRow;
  index: number;
  isCurrent: boolean;
  onClick?: () => void;
};

function RowPhoto({
  row,
  display,
}: {
  row: LeaderboardRow;
  display: MemberDisplay;
}): ReactNode {
  const src = row.photo
    ? pbFileUrl({ id: row.id, collectionId: row.collectionId }, row.photo)
    : '';
  return (
    <td className="py-sm pl-0 pr-0">
      <Avatar src={src} name={display.name} size="sm" />
    </td>
  );
}

function NameCell({ display }: { display: MemberDisplay }): ReactNode {
  return (
    <td className={TD}>
      <p className="text-[13px] font-medium text-text-primary">
        {display.fullName ?? display.name}
      </p>
      {display.handle && (
        <p className="text-[10px] text-text-tertiary">@{display.handle}</p>
      )}
    </td>
  );
}

function fmtFollowers(n: number): string {
  return n ? formatCompactFollowers(n) : '—';
}

function fmtTopCategory(row: LeaderboardRow): string {
  if (!row.topCategory || !row.topCreatorType) return '—';
  return categoryLabel(row.topCreatorType, row.topCategory);
}

const CAT_TD = `${TD} whitespace-nowrap text-xs`;

function RowData({
  row,
  display,
}: {
  row: LeaderboardRow;
  display: MemberDisplay;
}): ReactNode {
  const followers = fmtFollowers(row.avgFollowers);
  const ct =
    row.creatorCount > 0 ? 'text-text-primary' : 'text-text-tertiary/30';
  return (
    <>
      <NameCell display={display} />
      <td className={cn(NUM, ct)}>{row.creatorCount}</td>
      <td className={cn(NUM, 'text-text-secondary')}>{followers}</td>
      <td className={cn(CAT_TD, 'text-text-secondary')}>
        {fmtTopCategory(row)}
      </td>
      <td className={cn(NUM, 'text-text-tertiary')}>
        {formatCurrency(row.investment)}
      </td>
    </>
  );
}

export function LeaderboardRowItem({
  row,
  index,
  isCurrent,
  onClick,
}: Props): ReactNode {
  const display = useMemberDisplay(row);
  const trClass = cn(
    TR,
    onClick && TR_CLICK,
    isCurrent && 'bg-brand-red/[0.04]'
  );
  return (
    <tr onClick={onClick} className={trClass}>
      <td className={`${TD} text-[11px] text-text-tertiary/40`}>{index + 1}</td>
      <RowPhoto row={row} display={display} />
      <RowData row={row} display={display} />
    </tr>
  );
}
